import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import moment from 'moment/moment';
import { usePageContext } from '../../hooks/contexts/usePageContext';
import { CarouselMiniatures } from '../../components/2.0version/carouselMiniatures/CarouselMiniatures';
import { Section } from '../../components/2.0version/ot/section/Section';
import { OtTemplateQuery, SectionQuery } from '../../../graphql-types';
import * as styles from './otActivitesHebergements.module.scss';
import { EquipmentServices } from '../../components/2.0version/ot/section/content/equipmentServices/EquipmentServices';
import { Resume } from '../../components/2.0version/ot/section/content/resume/Resume';
import { IframeContact } from '../ingenie/IframeContact';
import { ContactInfos, Social } from '../../components/2.0version/contactInfos/ContactInfos';
import { WidgetDispoDiv, WidgetDispoResaInstance, WidgetResaDiv } from '../ingenie/WidgetResa';
import { WidgetReviews } from '../ingenie/WidgetReviews';
import { Openings } from '../../components/2.0version/openings/Openings';
import { PricesSection } from '../../components/2.0version/prices/Prices';
import { LocaleLink } from '../../components/2.0version/localeLink/LocaleLink';
import TripAdvisor from '../../../static/svg/tripAdvisor.svg';
import { Modal } from '../../components/2.0version/modal/Modal';
import { Button } from '../../components/2.0version/button/Button';
import { handleOpenItineraryMap } from '../../utils/openItineraryMap';

interface IOtActivitesHerbergementsProps {
  ot: OtTemplateQuery['ingenieOt'];
}

export const checkOpenings = (openings) => {
  const today = moment().format();
  const startInSixMonthMax = openings?.some((x) =>
    moment(today).isSameOrAfter(moment(x?.date_debut).subtract(6, 'months'))
  );
  const endIsNotOver = openings?.some((x) => moment(today).isSameOrBefore(x?.date_fin));
  return startInSixMonthMax && endIsNotOver;
};

export const OtActivitesHerbergements = ({ ot }: IOtActivitesHerbergementsProps) => {
  const { lang } = usePageContext();
  const { sanityObjetTouristique, allSanityListingSelection, sanityTranslations } =
    useStaticQuery<SectionQuery>(QUERY);
  const usableCodeForIngenieWidget = ot?.codePrestation ?? ot?.codePrestataire;
  const [open, setOpenModal] = useState(false);

  const carouselItems: any = ot?.medias?.map((media) => {
    const alt = media?.attributs?.find((attr) => attr?.type === 'ALT');
    return {
      _key: media?.id,
      url: media?.url,
      alt,
      type: 'image',
    };
  });

  /** put first image at the end */
  carouselItems.push(carouselItems.shift());

  const reviewsTripadvisor = ot?.liens?.filter((attr) => attr?.type === 'TRIPADVISOR');

  const wantedDesc = ['Description Commerciale', 'Complement Accueil'];

  const printWidgetResa = !allSanityListingSelection.nodes
    .filter((rubrique) =>
      ot?.parentRubriques?.find(
        (parentRubrique) => parentRubrique?.slug === rubrique?.slug?.current
      )
    )
    .find((rubrique) => rubrique.hideWidgetResa);

  const printWidgetDispo = !allSanityListingSelection.nodes
    .filter((rubrique) =>
      ot?.parentRubriques?.find(
        (parentRubrique) => parentRubrique?.slug === rubrique?.slug?.current
      )
    )
    .find((rubrique) => rubrique.hideWidgetDispo);

  return (
    <div className={styles.wrapper}>
      <div className={styles.grid}>
        <div className={styles.content}>
          <ul className={styles.description}>
            {ot?.descriptifs?.map((description, i) =>
              wantedDesc.includes(description?.libelle || '') ? (
                <>
                  <p
                    key={description?.libelle}
                    dangerouslySetInnerHTML={{
                      __html: description?.contenu?.[lang],
                    }}
                  />
                  {ot?.descriptifs && i + 1 !== ot?.descriptifs?.length && <br />}
                </>
              ) : null
            )}
          </ul>
          <CarouselMiniatures items={carouselItems} />
          {ot?.content?.resume && ot?.content?.resume?.length > 0 && (
            <Section title={sanityObjetTouristique?._rawResume[lang]}>
              <Resume data={ot?.content?.resume} />
            </Section>
          )}
          {ot?.content?.equipments && ot?.content?.equipments?.length > 0 && (
            <Section title={sanityObjetTouristique?._rawEquipmentServices[lang]}>
              <EquipmentServices data={ot?.content?.equipments} />
            </Section>
          )}
          {ot?.tarifs && ot?.tarifs?.length > 0 && (
            <Section title={sanityObjetTouristique?._rawPrices[lang]}>
              <PricesSection prices={ot?.tarifs} />
            </Section>
          )}
          {checkOpenings(ot?.ouvertures) && (
            <Section title={sanityObjetTouristique?._rawOpenings[lang]} hidden={!printWidgetDispo}>
              <Openings openings={ot?.ouvertures} />
            </Section>
          )}
          {ot?.reservable && usableCodeForIngenieWidget && (
            // && ot?.key !== 'P' && printWidgetResa && (
            <Section title={sanityObjetTouristique?._rawDisponibilities[lang]}>
              <WidgetDispoDiv codePrestation={usableCodeForIngenieWidget} />
            </Section>
          )}
          {ot?.reservable && usableCodeForIngenieWidget && (
            <Section className={styles.reviews} title={sanityObjetTouristique?._rawReviews[lang]}>
              <WidgetReviews codePrestation={usableCodeForIngenieWidget} />
            </Section>
          )}
          {!ot?.reservable &&
            reviewsTripadvisor &&
            reviewsTripadvisor.length > 0 &&
            usableCodeForIngenieWidget && (
              <Section title={sanityObjetTouristique?._rawReviews[lang]}>
                <LocaleLink
                  className={styles.link}
                  target="_blank"
                  to={reviewsTripadvisor?.[0]?.url}
                >
                  <div className={styles.svgWrapper}>
                    <TripAdvisor />
                  </div>
                </LocaleLink>
              </Section>
            )}
          {ot?.displayableContact && (
            <Section
              classNameChildren={styles.contactSection}
              title={sanityObjetTouristique?._rawContact[lang]}
            >
              <ContactInfos
                address={ot.reservable ? '' : ot?.displayableContact?.address ?? ''}
                tel={ot?.displayableContact?.tel ?? ''}
                webSite={ot.reservable ? '' : ot?.displayableContact?.webSite ?? ''}
                socials={ot?.socials as Social[]}
              />
              {ot?.displayableContact?.email && ot.codePrestataire && (
                <>
                  <Button onClick={() => setOpenModal(true)}>
                    {`${sanityTranslations?._rawContactLabel?.[lang]} ${ot?.libelle?.fr}`}
                  </Button>
                  <Modal fullScreenMobile open={open} setOpenModal={setOpenModal}>
                    <div className={styles.contactWrapper}>
                      <IframeContact
                        hebergeur={ot.key === 'H'}
                        codePrestataire={ot.codePrestataire}
                      />
                    </div>
                  </Modal>
                </>
              )}
            </Section>
          )}
          {ot?.localisation?.gps && (
            <Section title={sanityObjetTouristique?._rawMap[lang]}>
              <Button onClick={() => handleOpenItineraryMap(ot)}>
                {sanityTranslations?._rawMapLabel?.[lang]}
              </Button>
            </Section>
          )}
        </div>
        {ot?.reservable && usableCodeForIngenieWidget && (
          <div className={`${styles.widgetWrapper} ${!printWidgetResa ? styles.hidden : ''}`}>
            <div className={styles.widget}>
              <WidgetResaDiv codePrestation={usableCodeForIngenieWidget} />
            </div>
          </div>
        )}
      </div>
      {ot?.reservable && usableCodeForIngenieWidget && (
        <WidgetDispoResaInstance codePrestation={usableCodeForIngenieWidget} />
      )}
    </div>
  );
};

export const QUERY = graphql`
  query Section {
    sanityObjetTouristique {
      _rawAnnotations
      _rawContact
      _rawDetails
      _rawEquipmentServices
      _rawGeneralInformations
      _rawResume
      _rawReviews
      _rawMap
      _rawPracticalInformations
      _rawPrices
      _rawOpenings
      _rawDisponibilities
    }
    allSanityListingSelection {
      nodes {
        hideWidgetResa
        hideWidgetDispo
        slug {
          current
        }
      }
    }
    sanityTranslations {
      _rawContactLabel
      _rawMapLabel
    }
  }
`;
